import type { ExportFilter, ExportFilterOption, ExportFilterOptions } from "./types";

/** Options still linked to every upstream (`dependsOn`) filter that has a selection. */
export function visibleOptions(
  filter: ExportFilter,
  options: ExportFilterOptions,
  value: Record<string, string[]>,
): ExportFilterOption[] {
  const all = options[filter.key] ?? [];
  const deps = filter.dependsOn == null ? [] : ([] as string[]).concat(filter.dependsOn);
  return deps.reduce((visible, dep) => {
    const picked = value[dep] ?? [];
    if (picked.length === 0) return visible;
    const allowed = new Set(picked);
    return visible.filter((o) => {
      const linked = o.links?.[dep] ?? (o.parent != null ? [o.parent] : []);
      return linked.some((v) => allowed.has(v));
    });
  }, all);
}

export const ALL_OPTION_VALUE = "__all__";
export const DROPDOWN_OPTION_LIMIT = 20;

function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * What the dropdown lists: "Todas/Todos" plus the first `limit` options (already ranked by the
 * server) when the search is empty, otherwise the first `limit` matches on label or hint.
 */
export function dropdownOptions(
  options: ExportFilterOption[],
  input: string,
  limit = DROPDOWN_OPTION_LIMIT,
): ExportFilterOption[] {
  const needle = normalize(input);
  if (!needle) {
    const all = options.filter((o) => o.value === ALL_OPTION_VALUE);
    const rest = options.filter((o) => o.value !== ALL_OPTION_VALUE);
    return [...all, ...rest.slice(0, limit)];
  }
  const matches: ExportFilterOption[] = [];
  for (const o of options) {
    if (o.value === ALL_OPTION_VALUE) continue;
    if (normalize(o.label).includes(needle) || normalize(o.hint ?? "").includes(needle)) {
      matches.push(o);
      if (matches.length === limit) break;
    }
  }
  return matches;
}

/** Autocomplete value for a filter: the "Todas/Todos" option when nothing is picked. */
export function withAllOption(
  selected: ExportFilterOption[],
  allOption: ExportFilterOption,
): ExportFilterOption[] {
  return selected.length > 0 ? selected : [allOption];
}

/**
 * Selection after an Autocomplete change. Picking "Todas/Todos" clears the others;
 * picking anything else drops "Todas/Todos". Empty means all.
 */
export function pickedFromAutocomplete(
  previous: ExportFilterOption[],
  picked: ExportFilterOption[],
): string[] {
  const hadAll = previous.some((o) => o.value === ALL_OPTION_VALUE);
  const hasAll = picked.some((o) => o.value === ALL_OPTION_VALUE);
  if (hasAll && !hadAll) return [];
  return picked.filter((o) => o.value !== ALL_OPTION_VALUE).map((o) => o.value);
}

/** True while the filter's required upstream filter has no selection. */
export function isFilterLocked(filter: ExportFilter, value: Record<string, string[]>): boolean {
  return !!filter.requires && !(value[filter.requires]?.length);
}

/** Drops dependent selections that no longer match their upstream filters (in filter order). */
export function pruneDependents(
  filters: ExportFilter[],
  options: ExportFilterOptions,
  next: Record<string, string[]>,
): Record<string, string[]> {
  const out = { ...next };
  for (const f of filters) {
    if (isFilterLocked(f, out)) {
      if (out[f.key]?.length) out[f.key] = [];
      continue;
    }
    if (!f.dependsOn || !(out[f.key]?.length)) continue;
    const allowed = new Set(visibleOptions(f, options, out).map((o) => o.value));
    out[f.key] = (out[f.key] ?? []).filter((v) => allowed.has(v));
  }
  return out;
}
